import { HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { RequestService } from '@commonServices/request.service';
import { AccountLanguage } from '@interfaces/HttpClient/AccountApiPublicModels';
import { Client as AnalyticsApi, CatalogAdminCsvExportLanguage, FileResponse } from '@interfaces/HttpClient/AnalyticsApiModels';
import { firstValueFrom } from 'rxjs';
import { GlobalVarService } from './global-var.service';


@Injectable({
	providedIn: 'root'
})
export class CatalogAnalyticsReportService {

	constructor(private analyticsApi: AnalyticsApi,
		private requestService: RequestService,
		private globalVarService: GlobalVarService) { }

	async getYearsWithData(catalogId: number): Promise<number[]> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCsvExport_GetYearsWithData(catalogId));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getCsvExportAnalyticsMonthlyKPIs(catalogId: number, year: number): Promise<FileResponse> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCsvExport_GetCsvExportAnalyticsMonthlyKPIs(catalogId, year));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getCsvExportAnalyticsBasicUsersReportMonthly(catalogId: number): Promise<FileResponse> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCsvExport_GetCsvExportAnalyticsBasicUsersMonthly(catalogId, this.getCsvExportLanguage()));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			])
		}
	}

	async getCsvExportAnalyticsBasicUsersReportDaily(catalogId: number): Promise<FileResponse> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCsvExport_GetCsvExportAnalyticsBasicUsersDaily(catalogId, this.getCsvExportLanguage()));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			])
		}
	}

	async getCsvExportAnalyticsBasicTopCatalogItemsPerViewsMonthly(catalogId: number, topCount: number = 10): Promise<FileResponse> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCsvExport_GetCsvExportAnalyticsBasicTopCatalogItemsPerViewsMonthly(catalogId, this.getCsvExportLanguage(), topCount));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			])
		}
	}

	async getCsvExportAnalyticsBasicTopCatalogItemsPerExportsMonthly(catalogId: number, topCount: number = 10): Promise<FileResponse> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCsvExport_GetCsvExportAnalyticsBasicTopCatalogItemsPerExportsMonthly(catalogId, this.getCsvExportLanguage(), topCount));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			])
		}
	}

	async getCsvExportAnalyticsAdvancedUsersDaily(catalogId: number): Promise<FileResponse> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCsvExport_GetCsvExportAnalyticsAdvancedUsersDaily(catalogId, this.getCsvExportLanguage()))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			])
		}
	}

	async getCsvExportAnalyticsAdvancedUsersMonthly(catalogId: number): Promise<FileResponse> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCsvExport_GetCsvExportAnalyticsAdvancedUsersMonthly(catalogId, this.getCsvExportLanguage()));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			])
		}
	}

	async getCsvExportAnalyticsAdvancedExportsCountOfCatalogPerCatalogItem(catalogId: number, startDate: Date, endDate: Date): Promise<FileResponse> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCsvExport_GetCsvExportAnalyticsAdvancedExportsCountOfCatalogPerCatalogItem(catalogId, this.getCsvExportLanguage(), startDate, endDate));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			])
		}
	}

	async getCsvSessionsAnalyticsAdvancedSessionsCountOfCatalogPerCatalogItem(catalogId: number, startDate: Date, endDate: Date): Promise<FileResponse> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCsvExport_GetCsvExportAnalyticsAdvancedCatalogItemViewsCountOfCatalogPerCatalogItem(catalogId, this.getCsvExportLanguage(), startDate, endDate));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			])
		}
	}

	// Expert Cross Catalog
	async getCsvExportAnalyticsExpertsCrossCatalogSearchesMonthlyReportOfCatalog(catalogId: number): Promise<FileResponse> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCsvExport_GetCsvExportAnalyticsExpertsCrossCatalogSearchesMonthlyReportOfCatalog(catalogId, this.getCsvExportLanguage()));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			])
		}
	}
	async getCsvExportAnalyticsExpertsCrossCatalogTopSearchTermsMonthlyReportOfCatalog(catalogId: number): Promise<FileResponse> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCsvExport_GetCsvExportAnalyticsExpertsCrossCatalogTopSearchTermsMonthlyReportOfCatalog(catalogId, this.getCsvExportLanguage(), 10));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			])
		}
	}
	async getCsvExportAnalyticsExpertsCrossCatalogSearchesDailyReportOfCatalog(catalogId: number): Promise<FileResponse> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCsvExport_GetCsvExportAnalyticsExpertsCrossCatalogSearchesDailyReportOfCatalog(catalogId, this.getCsvExportLanguage()));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			])
		}
	}

	// Expert InCatalog
	async getCsvExportAnalyticsExpertsInCatalogFlopSearchTermsMonthlyReportOfCatalog(catalogId: number): Promise<FileResponse> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCsvExport_GetCsvExportAnalyticsExpertsInCatalogFlopSearchTermsMonthlyReportOfCatalog(catalogId, this.getCsvExportLanguage()));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			])
		}
	}
	async getCsvExportAnalyticsExpertsInCatalogSearchesDailyReportOfCatalog(catalogId: number): Promise<FileResponse> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCsvExport_GetCsvExportAnalyticsExpertsInCatalogSearchesDailyReportOfCatalog(catalogId, this.getCsvExportLanguage()));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			])
		}
	}
	async getCsvExportAnalyticsExpertsInCatalogSearchesMonthlyReportOfCatalog(catalogId: number): Promise<FileResponse> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCsvExport_GetCsvExportAnalyticsExpertsInCatalogSearchesMonthlyReportOfCatalog(catalogId, this.getCsvExportLanguage()));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			])
		}
	}
	async getCsvExportAnalyticsExpertsInCatalogTopSearchTermsMonthlyReportOfCatalog(catalogId: number): Promise<FileResponse> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCsvExport_GetCsvExportAnalyticsExpertsInCatalogTopSearchTermsMonthlyReportOfCatalog(catalogId, this.getCsvExportLanguage(), 10));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			])
		}
	}



	private getCsvExportLanguage(): CatalogAdminCsvExportLanguage {
		const language = this.globalVarService.getLanguage();
		let csvExportLanguage = CatalogAdminCsvExportLanguage.English;
		if (language === AccountLanguage.German) { csvExportLanguage = CatalogAdminCsvExportLanguage.German; }

		return csvExportLanguage;
	}
}
